import { Base_Url } from "../../BaseURL/BaseUrl";
import {
  getApiWithAuthorization,
  postApiWithAuthorization,
} from "../auth/ApiMethod";
import { emptyStringToNull } from "../utils/payload";

const normalizeStatus = (status) => {
  if (typeof status === "boolean") {
    return status ? "Active" : "Inactive";
  }

  if (typeof status === "string") {
    const normalized = status.trim().toLowerCase();
    return normalized === "active" ? "Active" : "Inactive";
  }

  return "Inactive";
};

const firstNonEmpty = (...values) =>
  values.find(
    (value) => value !== null && value !== undefined && String(value).trim() !== "",
  ) || "";

const normalizeEmployeeLookup = (item = {}) => ({
  employeeID: item.employeeID || item.employeeId || item.EmployeeID || null,
  employeeKeyID:
    item.employeeKeyID ||
    item.employeeKeyId ||
    item.EmployeeKeyID ||
    item.EmployeeKeyId ||
    item.keyID ||
    null,
  firstName: item.firstName || item.FirstName || "",
  lastName: item.lastName || item.LastName || "",
  fullName: firstNonEmpty(
    item.name,
    item.fullName,
    [item.firstName, item.lastName].filter(Boolean).join(" "),
  ),
});

const normalizeEmployee = (item = {}) => ({
  employeeID: item.employeeID || item.employeeId || null,
  employeeKeyID: item.employeeKeyID || item.employeeKeyId || item.keyID || null,
  firstName: item.firstName || "",
  lastName: item.lastName || "",
  fullName: firstNonEmpty(
    item.name,
    item.fullName,
    [item.firstName, item.lastName].filter(Boolean).join(" "),
  ),
  designationID: item.designationID || item.designationId || null,
  designationName: item.designationName || "",
  dateOfJoining: item.dateOfJoining || "",
  dateOfBirth: item.dateOfBirth || "",
  employeeCode: item.employeeCode || "",
  address: item.address || "",
  emailID: item.emailID || item.emailId || "",
  bloodGroupID: item.bloodGroupID || item.bloodGroupId || null,
  bloodGroupName: item.bloodGroupName || "",
  aadhaarNumber: item.aadhaarNumber || item.aadharNumber || "",
  panNumber: item.panNumber || "",
  roleTypeID: item.roleTypeID || item.roleTypeId || null,
  roleTypeName: item.roleTypeName || "",
  seniorID: item.seniorID || item.seniorId || null,
  seniorName: item.seniorName || "",
  alternativeNumber: item.alternativeNumber || "",
  mobileNumber: item.mobileNumber || "",
  password: item.password || "",
  employeeTypeID: item.employeeTypeID || item.employeeTypeId || null,
  employeeTypeName: item.employeeTypeName || "",
  officeID: item.officeID || item.officeId || null,
  officeName: item.officeName || "",
  createdOnDate: item.createdOnDate || "",
  status: normalizeStatus(item.status),
});

const normalizeListResponse = (response) => {
  const rows = Array.isArray(response?.responseData?.data)
    ? response.responseData.data.map(normalizeEmployee)
    : [];

  return {
    ...response,
    totalCount: response?.totalCount || rows.length,
    responseData: {
      ...response?.responseData,
      data: rows,
    },
  };
};

const normalizeModelResponse = (response) => ({
  ...response,
  responseData: {
    ...response?.responseData,
    data: normalizeEmployee(response?.responseData?.data),
  },
});

const normalizeLookupResponse = (response) => {
  const rows = Array.isArray(response?.responseData?.data)
    ? response.responseData.data.map(normalizeEmployeeLookup)
    : [];

  return {
    ...response,
    totalCount: response?.totalCount || rows.length,
    responseData: {
      ...response?.responseData,
      data: rows,
    },
  };
};

export const GetEmployeeList = async (params = {}) => {
  const response = await postApiWithAuthorization(
    `${Base_Url}api/Employee/GetEmployeeList`,
    params,
  );

  if (response?.statusCode === 200) {
    return normalizeListResponse(response);
  }

  return response;
};

export const GetEmployeeModel = async (employeeKeyID) => {
  const response = await getApiWithAuthorization(
    `${Base_Url}api/Employee/GetEmployeeModel?EmployeeKeyID=${encodeURIComponent(
      employeeKeyID,
    )}`,
  );

  if (response?.statusCode === 200) {
    return normalizeModelResponse(response);
  }

  return response;
};

export const AddUpdateEmployee = async (params = {}) =>
  postApiWithAuthorization(`${Base_Url}api/Employee/AddUpdateEmployee`, emptyStringToNull({
    userKeyID: params.userKeyID,
    employeeKeyID: params.employeeKeyID || null,
    firstName: params.firstName,
    lastName: params.lastName,
    designationID: Number(params.designationID),
    dateOfJoining: params.dateOfJoining,
    dateOfBirth: params.dateOfBirth,
    employeeCode: params.employeeCode,
    address: params.address,
    emailID: params.emailID,
    bloodGroupID: Number(params.bloodGroupID),
    aadhaarNumber: params.aadhaarNumber,
    panNumber: params.panNumber,
    roleTypeID: null,
    seniorID: params.seniorID ? Number(params.seniorID) : null,
    alternativeNumber: params.alternativeNumber || "",
    mobileNumber: params.mobileNumber,
    password: params.password,
    employeeTypeID: Number(params.employeeTypeID),
    officeID: params.officeID ? Number(params.officeID) : null,
  }));

export const GetAdminUserList = (params = {}) =>
  GetEmployeeList({
    ...params,
    RoleTypeID: 1,
  });

export const AddUpdateAdminUser = (params = {}) =>
  postApiWithAuthorization(
    `${Base_Url}api/Employee/AddUpdateEmployee`,
    emptyStringToNull({
      userKeyID: params.userKeyID,
      employeeKeyID: params.employeeKeyID || null,
      firstName: params.firstName,
      lastName: params.lastName,
      designationID: Number(params.designationID),
      dateOfJoining: null,
      dateOfBirth: null,
      employeeCode: null,
      address: params.address,
      emailID: params.emailID,
      bloodGroupID: null,
      aadhaarNumber: null,
      panNumber: null,
      RoleTypeID: 1,
      seniorID: null,
      alternativeNumber: params.alternativeNumber,
      mobileNumber: params.mobileNumber,
      password: params.password,
      employeeTypeID: null,
      officeID: null,
    }),
  );
export const ChangeEmployeeStatus = async (employeeKeyID, userKeyID) =>
  getApiWithAuthorization(
    `${Base_Url}api/Employee/ChangeEmployeeStatus?EmployeeKeyID=${encodeURIComponent(
      employeeKeyID,
    )}&UserKeyID=${encodeURIComponent(userKeyID)}`,
  );

export const GetEmployeeLookupList = async (
  roleTypeID,
  userKeyID,
  employeeTypeID,
  designationID,
) => {
  const query = new URLSearchParams();

  if (roleTypeID !== null && roleTypeID !== undefined && roleTypeID !== "") {
    query.append("RoleTypeID", roleTypeID);
  }

  if (userKeyID) {
    query.append("UserKeyID", userKeyID);
  }

  if (employeeTypeID !== null && employeeTypeID !== undefined && employeeTypeID !== "") {
    query.append("EmployeeTypeID", employeeTypeID);
  }

  if (designationID !== null && designationID !== undefined && designationID !== "") {
    query.append("DesignationID", designationID);
  }

  const response = await getApiWithAuthorization(
    `${Base_Url}api/Employee/GetEmployeeLookupList?${query.toString()}`,
  );

  if (response?.statusCode === 200) {
    return normalizeLookupResponse(response);
  }

  return response;
};

export const GetAllJuniorsLookupList = async (employeeKeyID) => {
  const response = await getApiWithAuthorization(
    `${Base_Url}api/Employee/GetAllJuniorsLookupList?EmployeeKeyID=${encodeURIComponent(
      employeeKeyID || "",
    )}`,
  );

  if (response?.statusCode === 200) {
    return normalizeLookupResponse(response);
  }

  return response;
};

export const GetEmployeeViewDetails = async (employeeKeyID) => {
  const response = await getApiWithAuthorization(
    `${Base_Url}api/Employee/GetEmployeeViewDetails?EmployeeKeyID=${encodeURIComponent(
      employeeKeyID,
    )}`,
  );

  if (response?.statusCode === 200) {
    return normalizeModelResponse(response);
  }

  return response;
};
